import { useEffect, useState } from 'react';
import type { MerchantRow } from '../../api/types';
import { Modal } from '../ui/Modal';

type EditFeesModalProps = {
  merchant: MerchantRow | null;
  open: boolean;
  onClose: () => void;
  onSave: (merchantId: string, feePercent: number) => Promise<void>;
};

export function EditFeesModal({ merchant, open, onClose, onSave }: EditFeesModalProps) {
  const [feePercent, setFeePercent] = useState('2.5');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (merchant && open) {
      setFeePercent(String(merchant.fee_percent ?? 2.5));
      setError(null);
    }
  }, [merchant, open]);

  const submit = async () => {
    if (!merchant) return;
    const value = Number.parseFloat(feePercent);
    if (!Number.isFinite(value) || value < 0 || value > 100) {
      setError('Enter a fee between 0 and 100');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await onSave(merchant.id, value);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Update failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Edit merchant fees"
      open={open && Boolean(merchant)}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn-outline" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary btn-inline"
            disabled={loading}
            onClick={() => void submit()}
          >
            {loading ? 'Saving…' : 'Save fees'}
          </button>
        </>
      }
    >
      {merchant ? (
        <>
          <p className="modal-muted">{merchant.name}</p>
          <label className="login-label" htmlFor="fee-percent">
            Commission (%)
          </label>
          <input
            id="fee-percent"
            className="login-input"
            type="number"
            min={0}
            max={100}
            step={0.1}
            value={feePercent}
            onChange={(e) => setFeePercent(e.target.value)}
          />
          {error ? <p className="form-error">{error}</p> : null}
        </>
      ) : null}
    </Modal>
  );
}
